import { PanelRightIcon } from "lucide-react";

import { formatAccessionCaseType } from "@blis/shared/models/accession-case-type";

import { StatIndicator } from "@/components/Indicators";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/icons";
import { Separator } from "@/components/ui/separator";
import { Sheet } from "@/components/ui/sheet";
import { useMediaQuery } from "@/components/ui/utils";
import { ITransplantSideDetail } from "@/features/integrations/components/ITransplantSideDetail";
import { format } from "@/lib/utils";
import { getReceiveTimezone } from "../lib/receive-timezone";
import { useAccession } from "./AccessionProvider";
import { ReceivedAtDisplay } from "./ReceivedAtDisplay";

export function AccessionSideDetail() {
  const { accession } = useAccession();
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const additionalReports = accession.additionalOrgs
    .map(({ org }) => org?.name)
    .concat(accession.additionalEmails || []);

  const splitBillingOrgs = accession.splitBillings.map(({ org }) => org?.name);

  const additionalIds = (accession.donorIds || []).filter((id) => !!id);

  return (
    <DataList
      className="pb-4"
      orientation={isMobile ? "vertical" : "horizontal"}
    >
      <DataListItem>
        <DataListLabel>Status</DataListLabel>
        <DataListValue>
          <StatusBadge status={accession.status || "new"} size="md" />
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Priority</DataListLabel>
        <DataListValue>
          {accession.priority === "STAT" ? (
            <span className="inline-flex items-center gap-1">
              <StatIndicator /> STAT
            </span>
          ) : (
            "Routine"
          )}
        </DataListValue>
      </DataListItem>
      <Separator className="col-span-2 my-2" />
      <DataListItem>
        <DataListLabel>Accession ID</DataListLabel>
        <DataListValue copyable>{accession.id.toString()}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Donor ID</DataListLabel>
        <DataListValue copyable>{accession.caseId}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>UNOS ID</DataListLabel>
        <DataListValue copyable>{accession.unosId}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Alternate ID</DataListLabel>
        <DataListValue className="min-w-0">
          <BetterList items={additionalIds} />
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Date of birth</DataListLabel>
        <DataListValue>{format(accession.dob, { time: false })}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Gender</DataListLabel>
        <DataListValue>
          {accession.sex === "M"
            ? "Male"
            : accession.sex === "F"
              ? "Female"
              : undefined}
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Case type</DataListLabel>
        <DataListValue>
          {formatAccessionCaseType(accession.caseType)}
        </DataListValue>
      </DataListItem>

      {/* Separator */}
      <Separator className="col-span-2 my-2" />
      <DataListItem>
        <DataListLabel>Client</DataListLabel>
        <DataListValue>{accession.org?.name}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Bill to</DataListLabel>
        <DataListValue>
          {accession.billToOrg?.name || accession.org?.name}
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Split billing</DataListLabel>
        <DataListValue className="min-w-0">
          <BetterList items={splitBillingOrgs} />
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Share with</DataListLabel>
        <DataListValue className="min-w-0">
          <BetterList items={additionalReports} />
        </DataListValue>
      </DataListItem>
      {/* Separator */}
      <Separator className="col-span-2 my-2" />
      <DataListItem className="items-center!">
        <DataListLabel>Created By</DataListLabel>
        <DataListValue>
          <div className="flex items-start gap-1">
            <UserAvatar
              size="small"
              user={accession.createdBy}
              className="-mt-0.5"
            />
            {accession.createdBy?.username}
          </div>
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Created At</DataListLabel>
        <DataListValue>{format(accession.date)}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Requisition ID</DataListLabel>
        <DataListValue copyable>{accession.requisitionId}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Packed at</DataListLabel>
        <DataListValue>
          {format(accession.packedAt, {
            showTz: true,
            zone: accession.packedTimezone,
          })}
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Tracking ID</DataListLabel>
        <DataListValue copyable>{accession.trackingId}</DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Receiving lab</DataListLabel>
        <DataListValue>{accession.receivingOrg?.name}</DataListValue>
      </DataListItem>
      <DataListItem className="items-center!">
        <DataListLabel>Received By</DataListLabel>
        <DataListValue>
          {accession.receivedBy ? (
            <div className="flex items-start gap-1">
              <UserAvatar
                size="small"
                user={accession.receivedBy}
                className="-mt-0.5"
              />
              {accession.receivedBy?.username}
            </div>
          ) : undefined}
        </DataListValue>
      </DataListItem>
      <DataListItem>
        <DataListLabel>Received At</DataListLabel>
        <DataListValue>
          <ReceivedAtDisplay
            receivedAt={accession.receivedAt}
            receiveTimezone={getReceiveTimezone(accession)}
          />
        </DataListValue>
      </DataListItem>
      <ITransplantSideDetail
        accessionId={accession.id}
        orgId={accession.orgId}
      />
    </DataList>
  );
}

export function AccessionSideDetailButton() {
  return (
    <DialogTrigger>
      <Button variant="ghost" size="icon" className="size-8">
        <PanelRightIcon />
      </Button>
      <Sheet side="right">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Details</DialogTitle>
          </DialogHeader>
          <DialogBody className="py-4">
            <AccessionSideDetail />
          </DialogBody>
        </Dialog>
      </Sheet>
    </DialogTrigger>
  );
}

function BetterList({
  items,
}: {
  items: string[] | undefined;
}): React.ReactNode | undefined {
  if (!items) {
    return undefined;
  }

  if (items.map((i) => i.trim()).filter((i) => !!i).length === 0) {
    return undefined;
  }

  if (items.length === 1) {
    return items[0];
  }
  return (
    <ul className="list-none pl-3.5 max-w-full">
      {items.map((item) => (
        <li key={item} className="relative">
          <span className="absolute left-[-1em] top-0 text-gray-400">-</span>
          <span className="block truncate" title={item}>
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}
